(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-aner-jewelry-visualizer]';
  const STONE_ROOT_SELECTOR = '[data-aner-main-stone-visualizer]';
  const imageCache = new Map();
  const boundsCache = new Map();

  // Characteristic luminance percentiles (1%, 5%, 25%, 50%, 75%, 95%, 99%)
  // for each finished metal. Used to remap the tone of a source photo whose
  // metal is darker than the selected one.
  const TONE_TARGETS = {
    unplated: [0.10, 0.24, 0.48, 0.70, 0.87, 0.965, 0.995],
    white: [0.11, 0.26, 0.50, 0.72, 0.88, 0.970, 0.995],
    rose: [0.10, 0.23, 0.46, 0.68, 0.86, 0.960, 0.990],
    yellow: [0.11, 0.25, 0.49, 0.70, 0.87, 0.965, 0.990],
    black: [0.11, 0.25, 0.49, 0.70, 0.87, 0.965, 0.990],
  };

  const PRESETS = {
    unplated: { target: [226, 231, 239], strength: 0.75, valueScale: 0.95, sourceKeep: 0.04, shadowKeep: 0.12, highlightLift: 0.04 },
    white: { target: [226, 231, 239], strength: 0.75, valueScale: 1, sourceKeep: 0.04, shadowKeep: 0.12, highlightLift: 0.04 },
    rose: { target: [210, 137, 119], strength: 0.65, valueScale: 1.07, sourceKeep: 0.08, shadowKeep: 0.28, highlightLift: 0 },
    yellow: { target: [207, 163, 72], strength: 0.72, valueScale: 1, sourceKeep: 0.08, shadowKeep: 0.28, highlightLift: 0.08, warmer: true },
    black: { target: [30, 32, 36], strength: 0.82, valueScale: 1, sourceKeep: 0.01, shadowKeep: 0.05, highlightLift: 0.03, blackCurve: true },
  };

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  function normalize(value) {
    return String(value || '').toLowerCase().replace(/[\s_\-–—]/g, '').trim();
  }

  function metalKey(value) {
    const normalized = normalize(value);
    if (/rose|玫瑰|玫金/.test(normalized)) return 'rose';
    if (/white|白金|白色金/.test(normalized)) return 'white';
    if (/yellow|黄金|黄k|金色/.test(normalized)) return 'yellow';
    if (/black|黑金|黑色金/.test(normalized)) return 'black';
    if (/unplated|s925|925|未电镀|不镀金/.test(normalized)) return 'unplated';
    return null;
  }

  function loadImage(url) {
    if (!url) return Promise.reject(new Error('Missing image URL'));
    if (!imageCache.has(url)) {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.src = url;
      imageCache.set(url, new Promise((resolve, reject) => {
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Unable to load ' + url));
      }));
    }
    return imageCache.get(url);
  }

  // Per-row silhouette of an image: where the opaque (or lit) pixels start and
  // end on every scanline, plus the overall bounds. One pass, cached per URL.
  function rowProfile(image, alphaMode) {
    const key = `${image.src}:${alphaMode}`;
    if (boundsCache.has(key)) return boundsCache.get(key);

    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;

    const left = new Int32Array(height).fill(-1);
    const right = new Int32Array(height).fill(-1);
    let top = -1;
    let bottom = -1;
    let minX = width;
    let maxX = -1;

    for (let y = 0; y < height; y += 1) {
      let lo = -1;
      let hi = -1;
      let hits = 0;
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        const value = alphaMode
          ? pixels[index + 3]
          : Math.max(pixels[index], pixels[index + 1], pixels[index + 2]);
        if (value > 32) {
          if (lo < 0) lo = x;
          hi = x;
          hits += 1;
        }
      }
      // A row needs a couple of real pixels so a lone speck is not a silhouette.
      if (hits < 2) continue;
      left[y] = lo;
      right[y] = hi;
      if (top < 0) top = y;
      bottom = y;
      if (lo < minX) minX = lo;
      if (hi > maxX) maxX = hi;
    }

    const profile = {
      width,
      height,
      left,
      right,
      top,
      bottom,
      minX: maxX < 0 ? 0 : minX,
      maxX: maxX < 0 ? width - 1 : maxX,
      empty: maxX < 0,
    };
    boundsCache.set(key, profile);
    return profile;
  }

  function profileBounds(profile) {
    if (profile.empty) return { x: 0, y: 0, width: profile.width, height: profile.height };
    return {
      x: profile.minX,
      y: profile.top,
      width: profile.maxX - profile.minX + 1,
      height: profile.bottom - profile.top + 1,
    };
  }

  // Luminance percentiles of the base photo inside the arm mask. Computed on a
  // small resample: percentiles do not need full resolution and this keeps the
  // page responsive. Cached per image pair.
  function armLuminancePercentiles(baseImage, maskImage) {
    const key = `tone:${baseImage.src}:${maskImage.src}`;
    if (boundsCache.has(key)) return boundsCache.get(key);
    const size = 320;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(baseImage, 0, 0, size, size);
    const baseData = context.getImageData(0, 0, size, size).data;
    context.clearRect(0, 0, size, size);
    context.drawImage(maskImage, 0, 0, size, size);
    const maskData = context.getImageData(0, 0, size, size).data;
    const values = [];
    for (let i = 0; i < baseData.length; i += 4) {
      if (maskData[i] > 127) {
        values.push((0.2126 * baseData[i] + 0.7152 * baseData[i + 1] + 0.0722 * baseData[i + 2]) / 255);
      }
    }
    values.sort((a, b) => a - b);
    const anchors = [0.01, 0.05, 0.25, 0.5, 0.75, 0.95, 0.99];
    const result = values.length
      ? anchors.map((p) => values[Math.min(values.length - 1, Math.floor(p * values.length))])
      : anchors.slice();
    boundsCache.set(key, result);
    return result;
  }

  function findMainImage() {
    const selectors = [
      '.hdt-product__media-item:not([hidden]) img',
      '.hdt-product-media__main .hdt-product__media-item img',
      '.t4s-product__media-item.is-selected img',
      '.t4s-product__media-item.is-active img',
      '.product__media-item.is-active img',
      '[data-product-media-container] img',
      '.product-single__media img',
      'product-gallery img',
    ];
    for (const selector of selectors) {
      const image = [...document.querySelectorAll(selector)].find((item) => item.offsetWidth > 0 && item.offsetHeight > 0);
      if (image) return image;
    }
    return null;
  }

  // Shops name the metal option differently from product to product
  // ("Plating", "Ring Type", "Band Color", ...), so an option is recognised
  // either by the configured name or by its values being metals we know.
  function metalOptionInput(productId, optionName) {
    const configured = normalize(optionName || '');
    return [...document.querySelectorAll('input[type="radio"][is-value]')].find((item) => {
      const formId = item.getAttribute('form') || item.closest('form')?.id || '';
      if (!productId || !formId.endsWith(productId)) return false;
      if (configured && normalize(item.name) === configured) return true;
      return metalKey(item.value) !== null;
    }) || null;
  }

  function findPlatingAnchor(productId, optionName) {
    const input = metalOptionInput(productId, optionName);
    if (!input) return null;
    return input.closest('fieldset, [data-option-name], .hdt-product-form__option, .hdt-product-form__options, .hdt-product-form__input, .hdt-product-form__item') || input.closest('label') || input;
  }

  function makeShader(gl, type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      const error = gl.getShaderInfoLog(shader);
      gl.deleteShader(shader);
      throw new Error(error || 'Shader compilation failed');
    }
    return shader;
  }

  function makeProgram(gl) {
    const vertex = makeShader(gl, gl.VERTEX_SHADER, `#version 300 es
      in vec2 a_position;
      out vec2 v_uv;
      void main() {
        v_uv = vec2((a_position.x + 1.0) * 0.5, (1.0 - a_position.y) * 0.5);
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `);
    const fragment = makeShader(gl, gl.FRAGMENT_SHADER, `#version 300 es
      precision highp float;
      uniform sampler2D u_base;
      uniform sampler2D u_metal_mask;
      uniform sampler2D u_stone_mask;
      uniform sampler2D u_stone;
      uniform vec3 u_target;
      uniform float u_strength;
      uniform float u_value_scale;
      uniform float u_source_keep;
      uniform float u_shadow_keep;
      uniform float u_highlight_lift;
      uniform float u_black_curve;
      uniform float u_warmer;
      uniform float u_has_metal;
      uniform float u_has_stone;
      uniform vec2 u_base_size;
      uniform vec2 u_source_size;
      uniform vec4 u_source_rect;
      uniform vec4 u_stone_box;
      uniform vec2 u_stone_origin;
      uniform vec2 u_stone_center;
      uniform float u_stone_scale;
      uniform float u_stone_rotation;
      uniform float u_mask_grow;
      uniform float u_tone_blend;
      uniform float u_metal_gamma;
      uniform float u_src_p[7];
      uniform float u_dst_p[7];
      in vec2 v_uv;
      out vec4 out_color;

      float luminance(vec3 color) {
        return dot(color, vec3(0.2126, 0.7152, 0.0722));
      }

      // Piecewise linear remap of the source metal tone onto the target
      // metal's characteristic tone. A dark source photo (black gold) cannot
      // become bright yellow gold by recolouring alone, which is why the tone
      // has to be remapped, not just tinted.
      float toneMapLuma(float l) {
        if (l <= u_src_p[0]) return u_dst_p[0];
        if (l >= u_src_p[6]) return u_dst_p[6];
        for (int i = 0; i < 6; i++) {
          float a = u_src_p[i];
          float b = u_src_p[i + 1];
          if (l >= a && l <= b) {
            float t = (b - a) > 0.0001 ? (l - a) / (b - a) : 0.0;
            return mix(u_dst_p[i], u_dst_p[i + 1], t);
          }
        }
        return l;
      }

      void main() {
        vec4 base = texture(u_base, v_uv);
        vec3 color = base.rgb;

        if (u_has_metal > 0.5) {
          float mask = texture(u_metal_mask, v_uv).r;
          vec3 target = u_target;
          float target_luma = max(luminance(target), 0.0001);
          vec3 delta = target - vec3(target_luma);
          // Tone remap: only lifts (a source metal darker than the target),
          // so photos that already match keep their approved look.
          float source_luma = luminance(color);
          float luma = mix(source_luma, toneMapLuma(source_luma), u_tone_blend);
          float highlight = clamp((luma - (u_black_curve > 0.5 ? 0.60 : 0.58)) / (u_black_curve > 0.5 ? 0.30 : 0.34), 0.0, 1.0);
          float shadow = 1.0 - clamp((luma - (u_black_curve > 0.5 ? 0.10 : 0.12)) / (u_black_curve > 0.5 ? 0.28 : 0.30), 0.0, 1.0);
          float mapped_luma = luma;
          if (u_black_curve > 0.5) {
            mapped_luma = luma <= 0.60
              ? 0.025 + 0.18 * pow(luma / 0.60, 2.2)
              : 0.205 + 0.70 * pow((luma - 0.60) / 0.40, 0.75);
          }
          float strength = u_strength * (1.0 - 0.78 * highlight - 0.45 * shadow);
          strength = clamp(strength, u_black_curve > 0.5 ? 0.12 : 0.06, u_strength);
          float source_keep = u_source_keep + u_shadow_keep * shadow;
          vec3 mapped = vec3(mapped_luma) + (mapped_luma / target_luma) * delta * strength + (color - vec3(source_luma)) * source_keep;
          float lift = 1.0 + u_highlight_lift * pow(highlight, u_black_curve > 0.5 ? 1.5 : 1.7);
          mapped *= u_value_scale * lift;
          if (u_warmer > 0.5) {
            float blue_scale = 1.0 - 0.06 * (1.0 - 0.45 * highlight);
            float new_blue = mapped.b * blue_scale;
            float luma_loss = 0.0722 * (mapped.b - new_blue);
            mapped.r += luma_loss * (0.2126 / 0.9278);
            mapped.g += luma_loss * (0.7152 / 0.9278);
            mapped.b = new_blue;
          }
          // Optional per-product lift for one metal: a gamma below 1 raises the
          // mid tones without clipping the highlights.
          vec3 finished = pow(clamp(mapped, 0.0, 1.0), vec3(u_metal_gamma));
          color = mix(color, finished, mask);
        }

        if (u_has_stone > 0.5) {
          // Work in image pixels so the stone keeps its aspect ratio exactly.
          // The placement box comes from the product's own stone mask, so no
          // per-product hand measurement is needed.
          vec2 point = v_uv * u_base_size - u_stone_center;
          float c = cos(-u_stone_rotation);
          float s = sin(-u_stone_rotation);
          point = vec2(c * point.x - s * point.y, s * point.x + c * point.y) + u_stone_center;
          vec2 source_pixel = u_source_rect.xy + (point - u_stone_origin) / u_stone_scale;
          vec2 stone_uv = source_pixel / u_source_size;
          vec4 stone = texture(u_stone, stone_uv);
          float inside = step(0.0, stone_uv.x) * step(stone_uv.x, 1.0)
                       * step(0.0, stone_uv.y) * step(stone_uv.y, 1.0);
          // Optional outward growth of the hand-made mask, so a mask that was
          // traced slightly inside the real stone cannot leave a rim of the
          // original gem behind. It never touches the mask file itself.
          // u_stone_center is in image pixels, so convert it before mixing it
          // with the UV coordinates of the current fragment.
          vec2 mask_center_uv = u_stone_center / u_base_size;
          vec2 mask_uv = mask_center_uv + (v_uv - mask_center_uv) / u_mask_grow;
          float mask_inside = step(0.0, mask_uv.x) * step(mask_uv.x, 1.0)
                            * step(0.0, mask_uv.y) * step(mask_uv.y, 1.0);
          float mask = texture(u_stone_mask, clamp(mask_uv, 0.0, 1.0)).r * mask_inside;
          float alpha = stone.a * mask * inside;
          color = mix(color, stone.rgb, clamp(alpha, 0.0, 1.0));
        }

        out_color = vec4(clamp(color, 0.0, 1.0), 1.0);
      }
    `);
    const program = gl.createProgram();
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'Program link failed');
    gl.deleteShader(vertex);
    gl.deleteShader(fragment);
    return program;
  }

  function createTexture(gl, image, unit) {
    const texture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0 + unit);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    // The vertex shader already maps V so that v_uv.y = 0 is the top of the
    // displayed canvas, so the texture must NOT be flipped on upload.
    // Flipping here as well mirrored the whole product photo vertically.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
  }

  class JewelryVisualizer {
    constructor(root) {
      this.root = root;
      this.stoneRoot = document.querySelector(STONE_ROOT_SELECTOR);
      this.stoneLabel = this.stoneRoot ? this.stoneRoot.querySelector('[data-aner-stone-label]') : null;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'aner-metal-preview-canvas aner-jewelry-preview-canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      this.canvas.addEventListener('webglcontextlost', (event) => {
        event.preventDefault();
        this.gl = null;
        this.program = null;
        this.textures.clear();
      });
      this.canvas.addEventListener('webglcontextrestored', () => {
        this.render();
      });
      this.gl = null;
      this.program = null;
      this.textures = new Map();
      this.baseImage = null;
      this.armMaskImage = null;
      this.stoneMaskImage = null;
      this.activeMetal = null;
      this.activeStone = null;
      this.geometryCache = new Map();
      this.renderGeneration = 0;
      this.attachScheduled = false;
      this.bindEvents();
      this.observeGallery();
      this.placeBelowPlating();
      this.attachCanvas();
    }

    bindEvents() {
      document.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof Element) || !this.isCurrentMetalControl(input)) return;
        const value = input.checked ? input.value : '';
        const key = metalKey(value);
        if (key) {
          this.activeMetal = key;
          this.render();
        }
      }, true);

      this.stoneRoot?.addEventListener('click', (event) => {
        const button = event.target.closest('.aner-main-stone-thumb');
        if (!button || !this.stoneRoot.contains(button)) return;
        event.preventDefault();
        this.activeStone = {
          key: button.dataset.anerStone,
          url: button.dataset.anerStoneSrc,
          button,
        };
        this.stoneRoot.querySelectorAll('.aner-main-stone-thumb').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
        if (this.stoneLabel) {
          const stoneName = button.getAttribute('aria-label') || button.textContent.trim();
          this.stoneLabel.textContent = stoneName ? ' ' + stoneName : '';
        }
        this.render();
      });
    }

    isCurrentMetalControl(element) {
      const label = element.closest('label');
      const linkedInput = label?.control || (label?.htmlFor ? document.getElementById(label.htmlFor) : null);
      const input = element.matches('input[type="radio"][is-value]') ? element : linkedInput || element.closest('input[type="radio"][is-value]');
      if (!input) return false;
      // The product id comes from the section, so the same asset serves every
      // product and no id is baked into this file.
      const productId = String(this.root.dataset.productId || '');
      const formId = input.getAttribute('form') || input.closest('form')?.id || '';
      if (!productId || !formId.endsWith(productId)) return false;
      const configured = normalize(this.root.dataset.optionName || '');
      if (configured && normalize(input.name) === configured) return true;
      // Products name this option differently ("Plating", "Ring Type", ...), so
      // any option whose values are metals we know counts as the metal option.
      return metalKey(input.value) !== null;
    }

    observeGallery() {
      this.observer = new MutationObserver(() => this.scheduleAttach());
      this.observer.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('resize', () => this.scheduleAttach(), { passive: true });
    }

    scheduleAttach() {
      if (this.attachScheduled) return;
      this.attachScheduled = true;
      requestAnimationFrame(() => {
        this.attachScheduled = false;
        this.placeBelowPlating();
        this.attachCanvas();
      });
    }

    placeBelowPlating() {
      const anchor = findPlatingAnchor(String(this.root.dataset.productId || ''), this.root.dataset.optionName || '');
      if (!anchor || !anchor.parentElement || !this.stoneRoot || this.stoneRoot === anchor) return;
      if (this.stoneRoot.previousElementSibling !== anchor) anchor.parentElement.insertBefore(this.stoneRoot, anchor.nextElementSibling);
    }

    attachCanvas() {
      const image = findMainImage();
      if (!image) return;
      const host = image.closest('.hdt-product__media-item, .hdt-product__media, .hdt-product-media__main, .t4s-product__media-item, .product__media-item, [data-product-media-container], .product-single__media') || image.parentElement;
      if (!host) return;
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      if (this.canvas.parentElement !== host) host.appendChild(this.canvas);
      this.galleryHost = host;
      if (this.baseImage) {
        this.canvas.width = this.baseImage.naturalWidth || this.baseImage.width;
        this.canvas.height = this.baseImage.naturalHeight || this.baseImage.height;
      }
      if (this.activeMetal || this.activeStone) this.canvas.classList.add('is-active');
    }

    async ensureImages() {
      if (!this.baseImage) this.baseImage = await loadImage(this.root.dataset.baseImage);
      if (this.activeMetal && !this.armMaskImage) this.armMaskImage = await loadImage(this.root.dataset.armMask);
      if (this.activeStone && !this.stoneMaskImage) this.stoneMaskImage = await loadImage(this.root.dataset.stoneMask);
      const stoneImage = this.activeStone ? await loadImage(this.activeStone.url) : null;
      return { stoneImage };
    }

    ensureGl() {
      if (this.gl && this.program) return true;
      this.gl = this.canvas.getContext('webgl2', { alpha: true, antialias: false, premultipliedAlpha: false, preserveDrawingBuffer: false });
      if (!this.gl) return false;
      this.program = makeProgram(this.gl);
      const gl = this.gl;
      const positions = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, positions);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const location = gl.getAttribLocation(this.program, 'a_position');
      gl.useProgram(this.program);
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, 2, gl.FLOAT, false, 0, 0);
      this.locations = {};
      ['u_base', 'u_metal_mask', 'u_stone_mask', 'u_stone', 'u_target', 'u_strength', 'u_value_scale', 'u_source_keep', 'u_shadow_keep', 'u_highlight_lift', 'u_black_curve', 'u_warmer', 'u_has_metal', 'u_has_stone', 'u_base_size', 'u_source_size', 'u_source_rect', 'u_stone_box', 'u_stone_origin', 'u_stone_center', 'u_stone_scale', 'u_stone_rotation', 'u_mask_grow', 'u_tone_blend', 'u_src_p[0]', 'u_dst_p[0]', 'u_metal_gamma'].forEach((name) => {
        this.locations[name] = gl.getUniformLocation(this.program, name);
      });
      return true;
    }

    stoneTuning() {
      let tuning = null;
      try {
        tuning = JSON.parse(this.root.dataset.stoneTuning || '');
      } catch (error) {
        tuning = null;
      }
      return tuning && typeof tuning === 'object' ? tuning : {};
    }

    // The product's own stone mask defines where the stone belongs. The mask
    // bounding box is used as the fit box, the stone is scaled by ONE factor
    // (so its aspect ratio never changes) and centred inside that box.
    // "cover" guarantees the whole masked area is filled, which is what a
    // stone replacement needs: no sliver of the original stone can survive.
    // A bounding box is not enough: two gems can share a box and still have
    // different silhouettes. Find the smallest uniform scale whose gem outline
    // covers the mask outline on every scanline, so no sliver of the original
    // stone can survive while the aspect ratio stays untouched.
    silhouetteCoverScale(context) {
      const { maskProfile, scaleX, scaleY, box, centerX, centerY, maskGrow, sourceBounds, sourceProfile } = context;
      const base = Math.max(box.width / sourceBounds.width, box.height / sourceBounds.height);
      const limit = base * 2.5;
      const maskCenterX = centerX / scaleX;
      const maskCenterY = centerY / scaleY;
      const halfHeight = (box.height * maskGrow) / scaleY / 2;
      const rows = [];

      for (let y = Math.ceil(centerY - (box.height * maskGrow) / 2); y <= Math.floor(centerY + (box.height * maskGrow) / 2); y += 2) {
        const maskY = Math.round(maskCenterY + (y / scaleY - maskCenterY) / maskGrow);
        if (maskY < 0 || maskY >= maskProfile.height) continue;
        const lo = maskProfile.left[maskY];
        const hi = maskProfile.right[maskY];
        if (lo < 0) continue;
        const grownLeft = maskCenterX + (lo - maskCenterX) / maskGrow;
        const grownRight = maskCenterX + (hi - maskCenterX) / maskGrow;
        rows.push([y, grownLeft * scaleX, grownRight * scaleX]);
      }
      if (!rows.length) return base;

      let scale = base;
      for (let step = 0; step < 260 && scale <= limit; step += 1) {
        const originX = centerX - (sourceBounds.width * scale) / 2;
        const originY = centerY - (sourceBounds.height * scale) / 2;
        let covered = true;
        for (let index = 0; index < rows.length; index += 1) {
          const [y, needLeft, needRight] = rows[index];
          const sourceY = Math.round(sourceBounds.y + (y - originY) / scale);
          if (sourceY < 0 || sourceY >= sourceProfile.height) { covered = false; break; }
          const stoneLeft = sourceProfile.left[sourceY];
          const stoneRight = sourceProfile.right[sourceY];
          if (stoneLeft < 0) { covered = false; break; }
          const haveLeft = originX + (stoneLeft - sourceBounds.x) * scale;
          const haveRight = originX + (stoneRight - sourceBounds.x) * scale;
          if (haveLeft > needLeft + 0.5 || haveRight < needRight - 0.5) { covered = false; break; }
        }
        if (covered) break;
        scale *= 1.004;
      }
      return Math.min(scale, limit);
    }

    getStoneGeometry(maskImage, baseImage, sourceProfile) {
      const tuning = this.stoneTuning();
      const baseWidth = baseImage.naturalWidth || baseImage.width;
      const baseHeight = baseImage.naturalHeight || baseImage.height;
      const maskWidth = maskImage.naturalWidth || maskImage.width;
      const maskHeight = maskImage.naturalHeight || maskImage.height;
      const maskProfile = rowProfile(maskImage, false);
      const bounds = profileBounds(maskProfile);
      const sourceBounds = profileBounds(sourceProfile);
      const scaleX = baseWidth / maskWidth;
      const scaleY = baseHeight / maskHeight;
      const box = {
        x: bounds.x * scaleX,
        y: bounds.y * scaleY,
        width: bounds.width * scaleX,
        height: bounds.height * scaleY,
      };
      const centerX = box.x + box.width / 2;
      const centerY = box.y + box.height / 2;
      const mode = tuning.fit === 'contain' ? 'contain' : 'cover';
      // Default is no growth: a hand-traced mask that sits exactly on the gem
      // edge must not be pushed outwards, or the new stone paints over the
      // setting. Raise maskGrow only for a product whose mask is known to be
      // traced inside the real gem.
      const maskGrow = Math.max(1, 1 + (Number.isFinite(Number(tuning.maskGrow)) ? Number(tuning.maskGrow) : 0));
      const multiplier = Number.isFinite(Number(tuning.scale)) ? Number(tuning.scale) : 1;
      const ratio = mode === 'contain'
        ? Math.min(box.width / sourceBounds.width, box.height / sourceBounds.height)
        : this.silhouetteCoverScale({
          maskProfile, scaleX, scaleY, box, centerX, centerY, maskGrow, sourceBounds, sourceProfile,
        });
      const scale = Math.max(ratio * multiplier, 1e-6);
      const drawnWidth = sourceBounds.width * scale;
      const drawnHeight = sourceBounds.height * scale;
      return {
        box,
        scale,
        originX: centerX - drawnWidth / 2 + (Number(tuning.offsetX) || 0),
        originY: centerY - drawnHeight / 2 + (Number(tuning.offsetY) || 0),
        centerX,
        centerY,
        rotation: Number(tuning.rotation) || 0,
        maskGrow,
      };
    }

    getTexture(key, image, unit) {
      if (!this.textures.has(key)) this.textures.set(key, createTexture(this.gl, image, unit));
      const texture = this.textures.get(key);
      this.gl.activeTexture(this.gl.TEXTURE0 + unit);
      this.gl.bindTexture(this.gl.TEXTURE_2D, texture);
      return texture;
    }

    async render() {
      if (!this.activeMetal && !this.activeStone) return;
      const generation = ++this.renderGeneration;
      try {
        const { stoneImage } = await this.ensureImages();
        if (generation !== this.renderGeneration) return;
        this.attachCanvas();
        if (!this.galleryHost || !this.ensureGl()) return;
        const gl = this.gl;
        const baseWidth = this.baseImage.naturalWidth || this.baseImage.width;
        const baseHeight = this.baseImage.naturalHeight || this.baseImage.height;
        this.canvas.width = baseWidth;
        this.canvas.height = baseHeight;
        gl.viewport(0, 0, baseWidth, baseHeight);
        gl.useProgram(this.program);
        this.getTexture('base', this.baseImage, 0);
        gl.uniform1i(this.locations.u_base, 0);
        gl.uniform1f(this.locations.u_has_metal, this.activeMetal ? 1 : 0);
        gl.uniform1f(this.locations.u_has_stone, this.activeStone ? 1 : 0);

        if (this.activeMetal) {
          const preset = PRESETS[this.activeMetal];
          this.getTexture('arm-mask', this.armMaskImage, 1);
          gl.uniform1i(this.locations.u_metal_mask, 1);
          gl.uniform3fv(this.locations.u_target, new Float32Array(preset.target.map((value) => value / 255)));
          gl.uniform1f(this.locations.u_strength, preset.strength);
          gl.uniform1f(this.locations.u_value_scale, preset.valueScale);
          gl.uniform1f(this.locations.u_source_keep, preset.sourceKeep);
          gl.uniform1f(this.locations.u_shadow_keep, preset.shadowKeep);
          gl.uniform1f(this.locations.u_highlight_lift, preset.highlightLift);
          gl.uniform1f(this.locations.u_black_curve, preset.blackCurve ? 1 : 0);
          gl.uniform1f(this.locations.u_warmer, preset.warmer ? 1 : 0);
          const sourceTone = armLuminancePercentiles(this.baseImage, this.armMaskImage);
          const targetTone = TONE_TARGETS[this.activeMetal];
          gl.uniform1fv(this.locations['u_src_p[0]'], new Float32Array(sourceTone));
          gl.uniform1fv(this.locations['u_dst_p[0]'], new Float32Array(targetTone));
          const toneGap = targetTone[3] - sourceTone[3];
          // toneLock keeps a product exactly as approved: the tone map is
          // disabled and the source photo's own tone is used unchanged.
          const toneLocked = this.stoneTuning().toneLock === true;
          gl.uniform1f(this.locations.u_tone_blend, toneLocked ? 0 : Math.max(0, Math.min(1, (toneGap - 0.03) / 0.07)));
          const gammaTable = this.stoneTuning().metalGamma;
          const metalGamma = gammaTable && Number.isFinite(Number(gammaTable[this.activeMetal]))
            ? Number(gammaTable[this.activeMetal])
            : 1;
          gl.uniform1f(this.locations.u_metal_gamma, metalGamma);
        }

        if (this.activeStone) {
          const mask = this.stoneMaskImage;
          const sourceProfile = rowProfile(stoneImage, true);
          const sourceBounds = profileBounds(sourceProfile);
          // The fit search costs a few tens of milliseconds, so remember it for
          // every later click on the same stone.
          const geometryKey = `${mask.src}|${this.activeStone.url}`;
          let geometry = this.geometryCache.get(geometryKey);
          if (!geometry) {
            geometry = this.getStoneGeometry(mask, this.baseImage, sourceProfile);
            this.geometryCache.set(geometryKey, geometry);
          }
          this.getTexture('stone-mask', mask, 2);
          this.getTexture(`stone:${this.activeStone.url}`, stoneImage, 3);
          gl.uniform1i(this.locations.u_stone_mask, 2);
          gl.uniform1i(this.locations.u_stone, 3);
          const sourceWidth = stoneImage.naturalWidth || stoneImage.width;
          const sourceHeight = stoneImage.naturalHeight || stoneImage.height;
          gl.uniform2f(this.locations.u_base_size, baseWidth, baseHeight);
          gl.uniform2f(this.locations.u_source_size, sourceWidth, sourceHeight);
          gl.uniform4f(this.locations.u_source_rect, sourceBounds.x, sourceBounds.y, sourceBounds.width, sourceBounds.height);
          gl.uniform4f(this.locations.u_stone_box, geometry.box.x, geometry.box.y, geometry.box.width, geometry.box.height);
          gl.uniform2f(this.locations.u_stone_origin, geometry.originX, geometry.originY);
          gl.uniform2f(this.locations.u_stone_center, geometry.centerX, geometry.centerY);
          gl.uniform1f(this.locations.u_stone_scale, geometry.scale);
          gl.uniform1f(this.locations.u_stone_rotation, geometry.rotation * Math.PI / 180);
          gl.uniform1f(this.locations.u_mask_grow, geometry.maskGrow);
        }

        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        if (generation === this.renderGeneration) this.canvas.classList.add('is-active');
      } catch (error) {
        console.warn('[ANER jewelry visualizer]', error.message);
      }
    }
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.anerVisualizerInstance === 'true') return;
      // Nothing to do when the section did not render its configuration.
      if (!root.dataset.productId || !root.dataset.baseImage) return;
      root.dataset.anerVisualizerInstance = 'true';
      new JewelryVisualizer(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
